import { useState, useEffect, useRef, useCallback } from 'react';
import { Send, StopCircle, Sparkles, ChevronDown, Loader2, X, History } from 'lucide-react';
import { useChatsStore, useModelsStore, useUserStore, useConfigStore, useSettingsStore } from '../../stores';
import { getChatById, createChat, updateChat, getModels } from '../../services/api';
import { useWebLlm } from '../WebLlmProvider';
import modelManager from '../../services/modelManager';
import mawiDB from '../../services/indexedDBService';
import { speechRecognitionService } from '../../services/speechRecognitionService';
import { Button } from '../../mawi-ui';
import { ScrollArea } from '../../mawi-ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../mawi-ui';
import { SkeletonChatMessage } from '../../mawi-ui';
import { LoadingSpinner, LoadingOverlay } from '../../mawi-ui';
import { Virtuoso } from 'react-virtuoso';
import { useKeyboardShortcuts, commonKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useTouchGestures } from '../../hooks/useTouchGestures';
import { useMemoryManagement } from '../../hooks/useMemoryManagement';
import { SmartSuggestions } from '../../mawi-ui';
import { ContentSummarization } from '../../mawi-ui';
import EnhancedMessage from '../chat/EnhancedMessage';
import AIChatInput from '../chat/AIChatInput';
import { ChatParameters, defaultParameters } from '../chat/ChatParameters';
import { cn, generateId, formatDate } from '../../utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface AIChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

// Advanced thinking indicator with progress and streaming support
interface ThinkingIndicatorProps {
  status?: string;
  progress?: number;
  isStreaming?: boolean;
}

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({ status, progress, isStreaming }) => {
  // Show model loading progress if model is loading
  if (status === 'loading' && progress !== undefined) {
    return (
      <div className="flex flex-col gap-2 py-2">
        <LoadingSpinner size="sm" text="Loading model..." />
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-xs text-gray-400">{Math.round(progress)}% loaded</span>
      </div>
    );
  }

  // Show streaming indicator for real-time responses
  if (isStreaming) {
    return (
      <div className="flex items-center gap-1 py-2">
        <span className="text-sm text-gray-500 mr-2">Generating</span>
        <div className="flex gap-1">
          <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse"></div>
          <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
          <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
        </div>
      </div>
    );
  }

  // Show thinking animation for initial response preparation
  return (
    <div className="flex items-center gap-1 py-2">
      <span className="text-sm text-gray-500 mr-2">Thinking</span>
      <div className="flex gap-1">
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
      </div>
    </div>
  );
};

export function AIChatPanel({ isOpen, onClose }: AIChatPanelProps) {
  const {
    currentChat,
    currentChatId,
    setCurrentChatId,
    setChatTitle,
    chatTitle,
    chats,
    setChats,
    addChat,
    updateChat: updateChatInStore,
  } = useChatsStore();
  const { models, setModels: setModelsStore } = useModelsStore();

  const { user } = useUserStore();
  const { config } = useConfigStore();
  const { settings } = useSettingsStore();
  const { engine, status, progress, currentModelId, setModelUrl, switchModel, isSupported, isStreaming, setIsStreaming, backend, cachedModels } = useWebLlm();

  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [micPermissionStatus, setMicPermissionStatus] = useState<'checking' | 'granted' | 'blocked' | 'prompt'>('checking');
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [chatParameters, setChatParameters] = useState<ChatParameters>(defaultParameters);
  const [modelSwitchInProgress, setModelSwitchInProgress] = useState(false);
  const [showParameters, setShowParameters] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [chatHistoryList, setChatHistoryList] = useState<any[]>([]);
  const [chatMode, setChatMode] = useState<string>('chat');

  // Memory management for chat component
  const memoryManager = useMemoryManagement({
    maxCacheSize: 100,
    cleanupInterval: 300000, // 5 minutes
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 50);
  }, [messages]);

  // Load models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const allModels = await modelManager.getAvailableModels();

        if (allModels.length === 0) {
          return;
        }

        const transformedModels = allModels.map(model => ({
          id: model.id,
          name: model.name,
          owned_by: 'arena' as const,
          size: model.size,
        }));

        setModelsStore(transformedModels);

        if (transformedModels.length > 0 && !selectedModel && !modelSwitchInProgress) {
          const defaultModelId = transformedModels[0].id;
          setSelectedModel(defaultModelId);
          setModelSwitchInProgress(true);
          switchModel(defaultModelId).catch(error => {
          }).finally(() => {
            setModelSwitchInProgress(false);
          });
        }
      } catch (error) {
      }
    };
    loadModels();
  }, [setModelsStore]);

  // Load last selected model on initial page load
  useEffect(() => {
    const loadLastSelectedModel = async () => {
      try {
        const selectedPrefs = await mawiDB.llm.getSelectedModelPreferences();
        if (selectedPrefs) {
          setSelectedModel(selectedPrefs.modelId);
          setModelSwitchInProgress(true);
          switchModel(selectedPrefs.modelId).catch(() => {
            setModelSwitchInProgress(false);
          });
        }
      } catch (err) {
      }
    };
    
    loadLastSelectedModel();
  }, []);

  // Load chat history list when panel is opened
  useEffect(() => {
    if (showChatHistory) {
      const loadChatHistory = async () => {
        try {
          const history = await mawiDB.store.getChatHistory(user?.id || 'anonymous');
          setChatHistoryList(history.sort((a, b) => 
            new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
          ));
        } catch (err) {
          setChatHistoryList([]);
        }
      };
      loadChatHistory();
    }
  }, [showChatHistory, user?.id]);

  // Update model URL when selectedModel changes
  useEffect(() => {
    if (selectedModel) {
      const modelUrl = modelManager.getModelUrl(selectedModel);
      if (modelUrl) {
        setModelUrl(modelUrl);
      }

      // Load model preferences from database or create defaults
      mawiDB.llm.getOrCreateModelPreferences(selectedModel, {
        temperature: defaultParameters.temperature,
        maxTokens: defaultParameters.maxTokens,
        topP: defaultParameters.topP,
        presencePenalty: defaultParameters.presencePenalty,
        frequencyPenalty: defaultParameters.frequencyPenalty,
        systemMessage: defaultParameters.systemMessage
      }).then(prefs => {
        setChatParameters({
          temperature: prefs.temperature,
          maxTokens: prefs.maxTokens,
          topP: prefs.topP,
          presencePenalty: prefs.presencePenalty,
          frequencyPenalty: prefs.frequencyPenalty,
          systemMessage: prefs.systemMessage
        });
      }).catch(err => {
      });
    }
  }, [selectedModel, setModelUrl]);

  // Auto-save chat parameters to database whenever they change
  useEffect(() => {
    if (selectedModel) {
      mawiDB.llm.updateModelPreferences(selectedModel, {
        ...chatParameters,
        isSelected: true
      }).catch(err => {
      });
    }
  }, [chatParameters, selectedModel]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    // Initialize model cache immediately if user sends message before 3 second timeout
    if ((window as any).initializeModelCacheNow) {
      await (window as any).initializeModelCacheNow();
    }

    const userMessage = {
      id: generateId(),
      role: 'user' as const,
      content: input,
      attachments: attachedFiles.length > 0 ? attachedFiles : undefined,
      timestamp: Date.now(),
    };

    const assistantMessage = {
      id: generateId(),
      role: 'assistant' as const,
      content: '',
      timestamp: Date.now(),
      done: false,
      model: selectedModel,
      modelName: models.find((m) => m.id === selectedModel)?.name,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInput('');
    setAttachedFiles([]);
    setIsLoading(true);

    // Create abort controller for cancellation
    const controller = new AbortController();
    setAbortController(controller);

    let response = '';

    try {
      if (engine && status === 'ready') {
        setIsStreaming(true);

        try {
          const chunks = await engine.chat.completions.create({
            messages: [
              { role: 'system', content: chatParameters.systemMessage },
              ...messages.map((m) => ({ role: m.role, content: m.content })),
              { role: 'user', content: input }
            ],
            stream: true,
            stream_options: { include_usage: true },
            temperature: chatParameters.temperature,
            max_tokens: chatParameters.maxTokens,
            top_p: chatParameters.topP,
            presence_penalty: chatParameters.presencePenalty,
            frequency_penalty: chatParameters.frequencyPenalty,
          });

          // Handle streaming response with cancellation support
          let fullResponse = '';
          for await (const chunk of chunks) {
            // Check if cancelled
            if (controller.signal.aborted) {
              break;
            }

            const delta = chunk.choices[0]?.delta.content || "";
            fullResponse += delta;

            // Update the assistant message content in real-time
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessage.id
                  ? { ...msg, content: fullResponse }
                  : msg
              )
            );
          }

          response = fullResponse || 'No response generated';
        } finally {
          setIsStreaming(false);
          setAbortController(null);
        }

        // Mark the message as done
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessage.id
              ? { ...msg, done: true }
              : msg
          )
        );
      } else {
        setIsStreaming(true);
        await new Promise(resolve => setTimeout(resolve, 500));

        const supportMessage = !isSupported
          ? ' WebLLM is not supported in this environment (requires WebAssembly and Cache API).'
          : '';

        const mockStatusMessage = status === 'error'
          ? 'Model loading failed. Try selecting a different model or check browser compatibility.'
          : status === 'loading'
          ? `Model is loading (${progress}%)... Please wait.`
          : `Model not ready (status: ${status}). Please select a model from the dropdown.`;

        try {
          // Simulate streaming for mock response
          const mockResponse = `${chatParameters.systemMessage ? `[System: ${chatParameters.systemMessage}]\n` : ''}I understand you said: "${input}". ${mockStatusMessage}${supportMessage}`;
          const words = mockResponse.split(' ');
          let currentResponse = '';

          for (const word of words) {
            currentResponse += (currentResponse ? ' ' : '') + word;
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessage.id
                  ? { ...msg, content: currentResponse }
                  : msg
              )
            );
            await new Promise(resolve => setTimeout(resolve, 100));
          }

          response = mockResponse;
        } finally {
          setIsStreaming(false);
        }

        // Mark the message as done
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessage.id
              ? { ...msg, done: true }
              : msg
          )
        );
      }
    } catch (error) {
      response = `Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessage.id ? { ...msg, content: response, done: true } : msg
        )
      );
    }

    setIsLoading(false);

    const fullMessages = [...messages, userMessage, { ...assistantMessage, content: response, done: true }];

    // ✅ Save chat to LOCAL IndexedDB FIRST (offline first)
    try {
      if (!currentChatId && messages.length === 0) {
        const title = input.slice(0, 50) + (input.length > 50 ? '...' : '');
        const chatId = generateId();

        // Save to local database
        await mawiDB.store.createChatHistory({
          userId: user?.id || 'anonymous',
          title,
          messages: fullMessages,
          modelId: selectedModel,
          totalTokens: 0,
          tags: [],
          isArchived: false
        });

        setCurrentChatId(chatId);
        setChatTitle(title);
      } else if (currentChatId) {
        // Update existing chat in local database
        await mawiDB.store.updateChatHistory(currentChatId, {
          messages: fullMessages,
          updatedAt: new Date().toISOString()
        });
      }
    } catch (localSaveErr) {
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Check microphone permissions
  const checkMicrophonePermissions = async (): Promise<boolean> => {
    try {
      // Check if Permissions API is supported
      if ('permissions' in navigator) {
        try {
          // Add timeout to prevent hanging
          const permissionPromise = navigator.permissions.query({ name: 'microphone' as PermissionName });
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Permissions API timeout')), 5000)
          );

          const permissionStatus = await Promise.race([permissionPromise, timeoutPromise]);

          if (permissionStatus.state === 'granted') {
            setMicPermissionStatus('granted');
            return true;
          } else if (permissionStatus.state === 'denied') {
            setMicPermissionStatus('blocked');
            return false;
          } else {
            setMicPermissionStatus('prompt');
            return false;
          }
        } catch (permissionError: any) {
        }
      }

      try {
        const mediaPromise = navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
        });
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('getUserMedia timeout')), 5000)
        );

        const stream = await Promise.race([mediaPromise, timeoutPromise]);
        stream.getTracks().forEach(track => track.stop());
        setMicPermissionStatus('granted');
        return true;
      } catch (mediaError: any) {
        if (mediaError.name === 'NotAllowedError') {
          setMicPermissionStatus('blocked');
        } else {
          setMicPermissionStatus('prompt');
        }
        return false;
      }
    } catch (error) {
      setMicPermissionStatus('prompt');
      return false;
    }
  };

  // Open browser microphone settings page
  const openMicrophoneSettings = () => {
    const userAgent = navigator.userAgent.toLowerCase();

    try {
      if (userAgent.includes('edg') || userAgent.includes('edge')) {
        window.open('edge://settings/content/microphone', '_blank');
      } else if (userAgent.includes('chrome') || userAgent.includes('chromium')) {
        window.open('chrome://settings/content/microphone', '_blank');
      } else if (userAgent.includes('firefox')) {
        window.open('about:preferences#privacy', '_blank');
      }
    } catch (error) {
    }
  };

  // Request microphone permissions - triggers browser's native popup
  const requestMicrophonePermissions = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      stream.getTracks().forEach(track => track.stop());
      setMicPermissionStatus('granted');
      return true;

    } catch (error: any) {
      setMicPermissionStatus('blocked');
      return false;
    }
  };

  // Check microphone permissions on component mount
  useEffect(() => {
    const initializePermissions = async () => {
      await checkMicrophonePermissions();
    };

    initializePermissions();
  }, []);

  // Initialize speech recognition service
  useEffect(() => {
    // Set up callbacks for the speech recognition service
    speechRecognitionService.setCallbacks({
      onStart: () => {
        setIsRecording(true);
      },
      onResult: (transcript: string, isFinal: boolean) => {
        if (isFinal) {
          setTranscript(transcript);
          setInput((prev) => prev + ' ' + transcript);
        }
      },
      onEnd: () => {
        setIsRecording(false);
      },
      onError: (error: string) => {
        setIsRecording(false);
      }
    });

    // Cleanup on unmount
    return () => {
      speechRecognitionService.setCallbacks({});
    };
  }, []);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: 'Enter',
        ctrlKey: true,
        action: () => {
          if (input.trim() && !isLoading) {
            sendMessage();
          }
        },
        description: 'Send message',
      },
    ],
  });

  // Copy input text to clipboard
  const copyInputToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(input);
    } catch (error) {
    }
  };

  const handleImageUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      setAttachedFiles(prev => [...prev, ...files]);
    };
    input.click();
  }, []);

  const handleCodeSnippet = useCallback(() => {
    const codeTemplate = `\`\`\`javascript
// Your code here
function example() {
    console.log("Hello, World!");
}
\`\`\``;

    setInput(prev => prev + (prev ? '\n\n' : '') + codeTemplate);
  }, []);

  const toggleRecording = async () => {
    // Check if speech recognition is supported
    if (!speechRecognitionService.isSupported()) {
      return;
    }

    // Check permissions first
    if (micPermissionStatus === 'blocked') {
      const granted = await requestMicrophonePermissions();
      if (!granted) return;
    }

    if (micPermissionStatus === 'prompt') {
      const granted = await requestMicrophonePermissions();
      if (!granted) return;
    }

    if (isRecording) {
      speechRecognitionService.stop();
    } else {
      setTranscript('');
      await speechRecognitionService.start();
    }
  };

  // Model persistence status
  const getModelStatus = () => {
    if (backend === 'service-worker') {
      return { text: 'Cached', color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900' };
    } else if (backend === 'web-worker') {
      return { text: 'Optimized', color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900' };
    }
    return null;
  };

  const modelStatus = getModelStatus();

  if (!isOpen) return null;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700">
      {/* Compact Header */}
      <div className="flex items-center justify-between px-2 py-1 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-1">
          {currentChatId && (
            <Button
              variant="default"
              size="sm"
              className="h-6 w-6 p-0 rounded-full bg-blue-500 hover:bg-blue-600 text-white text-xs"
              title="Start New Chat"
              onClick={() => {
                setCurrentChatId(null);
                setChatTitle('New Chat');
                setMessages([]);
                setShowChatHistory(false);
              }}
            >
              <span className="font-bold">+</span>
            </Button>
          )}
          
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              title="Chat History"
              onClick={() => setShowChatHistory(!showChatHistory)}
            >
              <History className="h-3.5 w-3.5" />
            </Button>

            {/* Chat History Panel */}
            {showChatHistory && (
              <div className="absolute left-0 top-full mt-1 w-64 max-h-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
                <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h4 className="font-semibold text-xs">Chat History</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={() => setShowChatHistory(false)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <ScrollArea className="max-h-48">
                  {chatHistoryList.length === 0 ? (
                    <div className="p-3 text-center text-gray-500 text-xs">
                      No previous chats found
                    </div>
                  ) : (
                    <div className="p-1">
                      {chatHistoryList.slice(0, 15).map((chat) => (
                        <button
                          key={chat.id}
                          className="w-full text-left p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-xs"
                          onClick={() => {
                            setCurrentChatId(chat.id);
                            setChatTitle(chat.title);
                            setMessages(chat.messages || []);
                            setShowChatHistory(false);
                          }}
                        >
                          <div className="font-medium truncate">{chat.title}</div>
                          <div className="text-[10px] text-gray-500">
                            {formatDate(chat.updatedAt || chat.createdAt)}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          {/* Model Status */}
          {status === 'ready' && (
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          )}
          {status === 'loading' && (
            <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
          )}
          {status === 'error' && (
            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
          )}
          
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="space-y-3">
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                'flex gap-1.5',
                message.role === 'user' ? 'flex-row-reverse' : ''
              )}
            >
              <div
                className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs',
                  message.role === 'user'
                    ? 'bg-blue-500 text-white'
                    : 'bg-green-500 text-white'
                )}
              >
                {message.role === 'user' ? (
                  <span className="text-[10px] font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                ) : (
                  <Sparkles className="h-2.5 w-2.5" />
                )}
              </div>
              <div
                className={cn(
                  'flex-1 max-w-[90%]',
                  message.role === 'user' ? 'text-right' : ''
                )}
              >
                <div
                  className="bg-gray-100 dark:bg-gray-800 rounded-lg p-2 text-xs"
                >
                  <div className="whitespace-pre-wrap">
                    {message.content}
                  </div>

                  {message.role === 'assistant' && !message.done && message.content.trim() === '' && (
                    <ThinkingIndicator status={status} progress={progress} isStreaming={isStreaming} />
                  )}
                  {message.role === 'assistant' && !message.done && message.content.trim() !== '' && (
                    <div className="flex items-center gap-1 mt-1">
                      <div className="flex gap-0.5">
                        <div className="w-0.5 h-0.5 bg-gray-400 rounded-full animate-pulse"></div>
                        <div className="w-0.5 h-0.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
                        <div className="w-0.5 h-0.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-1 h-5 px-1.5 text-[10px]"
                        onClick={() => {
                          if (abortController) {
                            abortController.abort();
                            setAbortController(null);
                          }
                          setIsLoading(false);
                          setIsStreaming(false);
                        }}
                      >
                        <StopCircle className="h-2.5 w-2.5 mr-0.5" />
                        Stop
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div ref={messagesEndRef} />
      </div>

      <AIChatInput
        modelStatus={modelStatus}
        input={input}
        onInputChange={setInput}
        onSend={sendMessage}
        onStop={() => {
          if (abortController) {
            abortController.abort();
            setAbortController(null);
          }
          setIsLoading(false);
          setIsStreaming(false);
        }}
        onVoiceToggle={toggleRecording}
        onFileAttach={() => fileInputRef.current?.click()}
        onImageUpload={handleImageUpload}
        isLoading={isLoading}
        isRecording={isRecording}
        isStreaming={isStreaming}
        micPermissionStatus={micPermissionStatus}
        attachedFiles={attachedFiles}
        onRemoveFile={(index) => setAttachedFiles(prev => prev.filter((_, i) => i !== index))}
        chatMode={chatMode}
        onChatModeChange={setChatMode}
        selectedModel={selectedModel}
        onModelChange={setSelectedModel}
        availableModels={models}
      />

        {/* Model Selector Modal */}
        {showModelSelector && (
          <div className="fixed inset-0 z-[9998] bg-black/50 dark:bg-black/70" onClick={() => setShowModelSelector(false)}>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999] max-h-80 overflow-y-auto">
              <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-sm">Select Model</h3>
              </div>

              <div className="p-1">
                {models.length === 0 ? (
                  <div className="text-center py-3 text-gray-500 dark:text-gray-400 text-xs">
                    Loading models...
                  </div>
                ) : (
                  models.map((model, index) => (
                    <button
                      key={model.id}
                      onClick={async () => {
                        if (modelSwitchInProgress) {
                          console.log('Model switch already in progress, ignoring');
                          return;
                        }

                        console.log('Model selected:', model.id);
                        setSelectedModel(model.id);
                        setShowModelSelector(false);
                        setModelSwitchInProgress(true);

                        try {
                          console.log('Calling switchModel for:', model.id);
                          await switchModel(model.id);
                          console.log('switchModel completed for:', model.id);
                        } catch (error) {
                          console.error('Failed to switch model:', error);
                        } finally {
                          setModelSwitchInProgress(false);
                        }
                      }}
                      className={`w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 rounded mb-0.5 ${
                        selectedModel === model.id
                          ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <span className="font-medium truncate">{model.name}</span>
                        {cachedModels.includes(model.id) && (
                          <span className="text-[10px] bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 px-1 py-0.5 rounded">
                            Cached
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{model.owned_by}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

export default AIChatPanel;
